import { useReactiveVar } from '@apollo/client';
import {
  AcademicCapIcon,
  BeakerIcon,
  BriefcaseIcon,
  ChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  DocumentDuplicateIcon,
  NewspaperIcon,
  PresentationChartLineIcon,
  ShieldExclamationIcon,
  ShoppingCartIcon,
  Squares2X2Icon,
  UserIcon,
  UsersIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import PageSkeleton from './PageSkeleton';
import { CollapseButton, TooltipWrapper, MenuItemText } from './styles';

import {
  ContactIcon,
  InvoiceIcon,
  PayrollRunsIcon,
  ReportsIcon,
  PayrollAuditIcon,
} from '@/assets/icons';
import BackgroundAuditIcon from '@/assets/icons/BackgroundAuditIcon';
import InvoiceGeneratorIconV2 from '@/assets/icons/InvoiceGeneratorIconV2';
import JobMonitorIconV2 from '@/assets/icons/JobMonitorIconV2';
import NotificationHubIconV2 from '@/assets/icons/NotificationHubIconV2';
import HeroIcon from '@/components/HeroIcon';
import NavigationMenu from '@/components/NavigationMenu';
import Stack from '@/components/Stack';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { useAppDispatch } from '@/hooks/store';
import { Role } from '@/routes/PrivateRoute';
import ROUTES from '@/routes/routes';
import { setRefetchDashboardData } from '@/store/slices/layoutSlice';
import { currentAdminVar, currentAgencyVar } from '@/util/apollo/cache';
import { useFeatureValue } from '@growthbook/growthbook-react';
import useAuth from '@/hooks/useAuth';
import { useNoshowSidebarStats } from '@/services/noshowAlertsAPI';

interface SidebarProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const SidebarV2 = ({ isCollapsed, onToggleCollapse }: SidebarProps) => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const currentAdmin = useReactiveVar(currentAdminVar);
  const ReleaseNotesAdmin = useFeatureValue(
    FEATURE_TOGGLE.ReleaseNotesAdmin,
    false,
  );
  const { currentAdminIsCustomerAdmin: isClientAdmin } = useAuth();
  const { needsActionCount, backfillCount } = useNoshowSidebarStats();

  const dispatch = useAppDispatch();
  const dashboardClick = () => {
    if (window.location.pathname !== ROUTES.basePath) {
      dispatch(setRefetchDashboardData(true));
    }
  };

  const renderIcon = (IconComponent: any, a11yLabel: string) => {
    const icon = <HeroIcon icon={IconComponent} />;
    return () =>
      isCollapsed ? (
        <TooltipWrapper data-tooltip={a11yLabel}>{icon}</TooltipWrapper>
      ) : (
        icon
      );
  };

  const renderCustomIcon = (Icon: any, label: string) => {
    return () =>
      isCollapsed ? (
        <TooltipWrapper data-tooltip={label}>
          <Icon />
        </TooltipWrapper>
      ) : (
        <Icon />
      );
  };

  const renderNoshowIcon = () => {
    const icon = <HeroIcon icon={ShieldExclamationIcon} />;
    const badge = needsActionCount > 0 && (
      <span
        style={{
          background: '#E53E3E',
          color: 'white',
          borderRadius: '50%',
          width: '16px',
          height: '16px',
          fontSize: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          flexShrink: 0,
          marginLeft: '-6px',
          marginTop: '-8px',
        }}
      >
        {needsActionCount > 9 ? '9+' : needsActionCount}
      </span>
    );
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'flex-start',
          overflow: 'visible',
        }}
      >
        {isCollapsed ? (
          <TooltipWrapper data-tooltip="No-Show Alerts">{icon}</TooltipWrapper>
        ) : (
          icon
        )}
        {badge}
      </span>
    );
  };

  const adminNavigation = useMemo(
    () => [
      {
        id: 'menu-general',
        items: [
          {
            CustomIcon: renderIcon(Squares2X2Icon, 'Dashboard'),
            a11yLabel: 'Dashboard',
            to: ROUTES.basePath,
            end: true,
            id: 'dashboard-link',
            onClick: dashboardClick,
          },
          {
            CustomIcon: renderIcon(ChatBubbleOvalLeftEllipsisIcon, 'Chat'),
            // The unread dot keys off the 'Chat V2' a11yLabel
            // (NavigationMenu.tsx); the visible label stays "Chat".
            a11yLabel: 'Chat V2',
            label: 'Chat',
            to: ROUTES.inbox,
            end: true,
            id: 'inbox-link',
          },
          {
            CustomIcon: renderIcon(ChatBubbleOvalLeftEllipsisIcon, 'Chat'),
            a11yLabel: 'Chat',
            to: ROUTES.chat,
            end: true,
            id: 'chat-link',
          },
          {
            CustomIcon: renderIcon(DocumentTextIcon, 'Pending Uniforms'),
            a11yLabel: 'Pending Uniforms',
            label: 'Pending Uniforms',
            to: `/${ROUTES.skillManagement}/${ROUTES.pendingUniforms}`,
            id: 'pending-uniforms-link',
          },
          {
            CustomIcon: renderIcon(AcademicCapIcon, 'Pending Certificates'),
            a11yLabel: 'Pending Certificates',
            label: 'Pending Certificates',
            to: `/${ROUTES.skillManagement}/${ROUTES.pendingCertificates}`,
            id: 'pending-certificates-link',
          },
          {
            CustomIcon: renderIcon(AcademicCapIcon, 'Badges'),
            a11yLabel: 'Badges',
            label: 'Badges',
            to: `/${ROUTES.skillManagement}/${ROUTES.badges}`,
            id: 'badges-link',
          },
          {
            CustomIcon: renderIcon(ClipboardDocumentListIcon, 'Violations'),
            a11yLabel: 'Violations',
            label: 'Violations',
            to: ROUTES.tasks,
            id: 'accountability-link',
          },
          ...(currentAdmin?.role === Role.TENANT_ADMIN
            ? [
                {
                  CustomIcon: renderIcon(
                    ClipboardDocumentCheckIcon,
                    'AI Assessments',
                  ),
                  a11yLabel: 'AI Assessments',
                  to: ROUTES.aiAssessments,
                  id: 'ai-assessments-link',
                },
                {
                  CustomIcon: renderIcon(ChartBarIcon, 'Analytics'),
                  a11yLabel: 'Analytics',
                  label: 'Analytics',
                  to: `/${ROUTES.analytics}`,
                  // NavigationMenu renders `nav-${item.id}` for both the DOM id
                  // and the PermissionComponent lookup, so this must be the
                  // BARE id like every sibling. Carrying the `nav-` prefix here
                  // produced `nav-nav-analytics-link`, which (a) broke the
                  // documented `#nav-analytics-link` selector and (b) missed
                  // roles.json entirely, so the declared TENANT_ADMIN gate
                  // silently fell through to hasUserRights' permissive default.
                  id: 'analytics-link',
                },
                {
                  CustomIcon: renderIcon(BeakerIcon, 'Feature Flags'),
                  a11yLabel: 'Feature Flags',
                  label: 'Feature Flags',
                  to: ROUTES.featureFlags,
                  id: 'feature-flags-link',
                },
                {
                  CustomIcon: renderIcon(
                    PresentationChartLineIcon,
                    'Analytics Data',
                  ),
                  a11yLabel: 'Analytics Data',
                  label: 'Analytics Data',
                  to: `/${ROUTES.analyticsDashboard}`,
                  // Bare id, same trap as the 'analytics-link' entry above —
                  // NavigationMenu renders `nav-${item.id}`.
                  id: 'analytics-dashboard-link',
                },
              ]
            : []),
          {
            CustomIcon: renderCustomIcon(JobMonitorIconV2, 'Shift Monitor'),
            a11yLabel: 'Shift Monitor',
            to: ROUTES.jobmonitor,
            id: 'jobmonitor-link',
          },
          ...(!isClientAdmin
            ? [
                {
                  CustomIcon: () => renderNoshowIcon(),
                  a11yLabel: 'No-Show Alerts',
                  label:
                    backfillCount > 0
                      ? `No-Show Alerts (${backfillCount} backfill)`
                      : 'No-Show Alerts',
                  to: ROUTES.noshowAlerts,
                  id: 'noshow-alerts-link',
                },
              ]
            : []),
        ],
      },
      {
        id: 'menu-gravywork-team',
        title: 'GravyWork Team',
        withBorder: true,
        items: [
          {
            CustomIcon: renderIcon(BriefcaseIcon, 'Clients'),
            a11yLabel: 'Clients',
            to: ROUTES.clients,
            id: 'clients-link',
          },
          {
            CustomIcon: renderIcon(ContactIcon, 'Contacts'),
            a11yLabel: 'Contacts',
            to: ROUTES.contacts,
            id: 'contacts-link',
          },
          {
            CustomIcon: renderIcon(UsersIcon, 'GravyWorkers'),
            a11yLabel: 'GravyWorkers',
            to: ROUTES.workersV2,
            id: 'workers-link',
          },
        ],
      },
      {
        id: 'menu-admin',
        title: 'Admin',
        withBorder: true,
        items: [
          {
            CustomIcon: renderIcon(ShoppingCartIcon, 'Orders'),
            a11yLabel: 'Orders',
            to: ROUTES.orders,
            id: 'orders-link',
          },
          {
            CustomIcon: renderIcon(InvoiceIcon, 'Invoices'),
            a11yLabel: 'Invoices',
            to: ROUTES.invoices,
            id: 'invoices-link',
          },
          // Invoice Health is a GravyWork-internal billing tool — tenant admins
          // only. Strict role check, so helpdesk admins are excluded too, not
          // just client admins.
          ...(currentAdmin?.role === Role.TENANT_ADMIN
            ? [
                {
                  CustomIcon: renderIcon(
                    ShieldExclamationIcon,
                    'Invoice Health',
                  ),
                  a11yLabel: 'Invoice Health',
                  to: ROUTES.invoiceHealth,
                  id: 'invoice-health-link',
                },
              ]
            : []),
          {
            CustomIcon: renderCustomIcon(
              InvoiceGeneratorIconV2,
              'Invoice Generator',
            ),
            a11yLabel: 'Invoice Generator',
            to: ROUTES.invoiceGenerator,
            id: 'inv-generator-link',
          },
          {
            CustomIcon: renderCustomIcon(PayrollRunsIcon, 'Payroll Runs'),
            a11yLabel: 'Payroll Runs',
            to: ROUTES.payrolls,
            id: 'payrolls-link',
          },
          // {
          //   CustomIcon: renderCustomIcon(
          //     PayrollRunsIcon,
          //     'Payroll Runs (Zeal)',
          //   ),
          //   a11yLabel: 'Payroll Runs (Zeal)',
          //   to: ROUTES.payrollsV2,
          //   id: 'payrolls-v2-link',
          // },
          ...(!isClientAdmin
            ? [
                {
                  CustomIcon: renderCustomIcon(
                    PayrollAuditIcon,
                    'Payroll Audit',
                  ),
                  a11yLabel: 'Payroll Audit',
                  to: ROUTES.payrollAudit,
                  id: 'payrollaudit-link',
                },
              ]
            : []),
          {
            CustomIcon: renderCustomIcon(ReportsIcon, 'Reports'),
            a11yLabel: 'Reports',
            to: ROUTES.reports,
            id: 'reports-link',
          },
          ...(ReleaseNotesAdmin
            ? [
                {
                  CustomIcon: renderCustomIcon(
                    NotificationHubIconV2,
                    'Notification Hub',
                  ),
                  a11yLabel: 'Notification Hub',
                  to: ROUTES.notificationHub,
                  id: 'notificationhub-link',
                },
              ]
            : []),
          ...(!isClientAdmin
            ? [
                {
                  CustomIcon: renderCustomIcon(
                    BackgroundAuditIcon,
                    'Background Audit',
                  ),
                  a11yLabel: 'Background Audit',
                  to: ROUTES.backgroundAudit,
                  id: 'backgroundaudit-link',
                },
                {
                  CustomIcon: renderIcon(DocumentTextIcon, 'HelloSign Audit'),
                  a11yLabel: 'HelloSign Audit',
                  to: ROUTES.hellosignAudit,
                  id: 'hellosign-audit-link',
                },
                {
                  CustomIcon: renderIcon(NewspaperIcon, 'Posts'),
                  a11yLabel: 'Posts',
                  to: ROUTES.posts,
                  id: 'posts-link',
                },
                {
                  CustomIcon: renderIcon(DocumentDuplicateIcon, 'Documents'),
                  a11yLabel: 'Documents',
                  to: ROUTES.documents,
                  id: 'documents-link',
                },
                {
                  CustomIcon: renderIcon(
                    DevicePhoneMobileIcon,
                    'Version Controls',
                  ),
                  a11yLabel: 'Version Controls',
                  to: ROUTES.versionControls,
                  id: 'version-controls-link',
                },
                {
                  CustomIcon: renderIcon(
                    ChatBubbleOvalLeftEllipsisIcon,
                    'SMS Logs',
                  ),
                  a11yLabel: 'SMS Logs',
                  to: ROUTES.smsLogs,
                  id: 'sms-logs-link',
                },
                // {
                //   CustomIcon: renderIcon(
                //     ShieldExclamationIcon,
                //     'No-Show Alerts',
                //   ),
                //   a11yLabel: 'No-Show Alerts',
                //   to: ROUTES.noshowAlerts,
                //   id: 'noshow-alerts-link',
                // },
                // {
                //   CustomIcon: renderIcon(EnvelopeOpenIcon, 'Order Invites'),
                //   a11yLabel: 'Order Invites',
                //   to: ROUTES.orderInvites,
                //   id: 'orderinvites-link',
                // },
              ]
            : []),
        ],
      },
      {
        id: 'menu-my-information',
        title: 'My Information',
        items: [
          {
            CustomIcon: renderIcon(UsersIcon, 'My Team'),
            a11yLabel: 'My Team',
            to: ROUTES.myTeam,
            id: 'myteam-link',
          },
          {
            CustomIcon: renderIcon(BriefcaseIcon, 'My Company'),
            a11yLabel: 'My Company',
            to: ROUTES.myCompany,
            id: 'mycompany-link',
          },
          {
            CustomIcon: renderIcon(UserIcon, 'My Profile'),
            a11yLabel: 'My Profile',
            to: ROUTES.myProfile,
            id: 'profile-link',
          },
        ],
      },
    ],
    [
      currentAdmin,
      ReleaseNotesAdmin,
      isCollapsed,
      needsActionCount,
      backfillCount,
    ],
  );

  const renderMenuText = (text: string) => {
    return <MenuItemText isCollapsed={isCollapsed}>{text}</MenuItemText>;
  };

  return (
    <Stack vertical css={{ position: 'relative', paddingBottom: '24px' }}>
      <CollapseButton onClick={onToggleCollapse} isCollapsed={isCollapsed}>
        <HeroIcon
          icon={isCollapsed ? ChevronRightIcon : ChevronLeftIcon}
          size="xs"
        />
      </CollapseButton>

      {currentAdmin ? (
        <NavigationMenu
          groups={adminNavigation}
          isCollapsed={isCollapsed}
          renderText={renderMenuText}
        />
      ) : (
        <PageSkeleton />
      )}
    </Stack>
  );
};

export default SidebarV2;
